import React, { createContext, useContext, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { startSessionTracking, endSessionTracking, resumeSessionTracking } from '../lib/sessionTracker';

interface User {
  id: string;
  name: string;
  displayName: string;
  role: 'seller' | 'manager' | 'motoboy' | 'admin';
  ruta: number;
  unit_id: string | null;
  unit_name: string | null;
  allowed_rutas: number[] | null;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const loadUserData = async (userId: string) => {
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('id, email, role, ruta, unit_id, display_name, allowed_rutas, units!users_unit_id_fkey(name)')
      .eq('id', userId)
      .maybeSingle();

    if (userError) throw userError;
    if (!userData) throw new Error('No user data found');

    const emailPrefix = userData.email.split('@')[0] || '';
    const unitName = (userData.units as any)?.name || null;
    setUser({
      id: userData.id,
      name: emailPrefix,
      displayName: userData.display_name || emailPrefix,
      role: userData.role as 'seller' | 'manager' | 'motoboy' | 'admin',
      ruta: userData.ruta,
      unit_id: userData.unit_id,
      unit_name: unitName,
      allowed_rutas: (userData as any).allowed_rutas ?? null
    });
  };

  const handleSession = async (session: any) => {
    if (session?.user) {
      try {
        await loadUserData(session.user.id);
        resumeSessionTracking().catch((err) => console.warn('resume session tracking failed', err));
      } catch (error) {
        console.error('Error loading user data during session restoration:', error);
        setUser(null);
      }
    } else {
      setUser(null);
    }
    setLoading(false);
  };

  React.useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      handleSession(session);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'SIGNED_OUT') {
          setUser(null);
          setLoading(false);
        } else if (event === 'TOKEN_REFRESHED' && session) {
          (async () => {
            try {
              await loadUserData(session.user.id);
            } catch (error) {
              console.error('Error refreshing user data:', error);
            }
          })();
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const refreshUser = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      await loadUserData(session.user.id);
    }
  };

  const login = async (email: string, password: string) => {
    setLoading(true);
    try {
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password: password.trim()
      });

      if (signInError) {
        if (signInError.message.includes('Invalid login credentials')) {
          throw new Error('E-mail ou senha incorretos');
        }
        if (signInError.message.includes('Email not confirmed')) {
          throw new Error('E-mail não confirmado. Verifique sua caixa de entrada.');
        }
        throw new Error('Erro ao fazer login: ' + signInError.message);
      }

      if (!signInData.user) {
        throw new Error('Erro ao fazer login: dados do usuário não encontrados');
      }

      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('role, id, ruta, unit_id')
        .eq('id', signInData.user.id)
        .maybeSingle();

      if (userError) {
        await supabase.auth.signOut();
        throw new Error('Erro ao verificar usuário');
      }

      if (!userData) {
        await supabase.auth.signOut();
        throw new Error('Usuário não encontrado no sistema');
      }

      if (userData.role !== 'admin' && !userData.unit_id) {
        await supabase.auth.signOut();
        throw new Error('Usuário não está associado a nenhuma unidade. Entre em contato com o administrador.');
      }

      await loadUserData(signInData.user.id);
      try {
        await endSessionTracking();
      } catch {
        // ignore any previous session cleanup errors
      }
      startSessionTracking().catch((err) => console.warn('failed to start session tracking', err));
      navigate('/dashboard');
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await endSessionTracking();
    } catch (err) {
      console.warn('failed to end session tracking', err);
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (session && session.expires_at && new Date(session.expires_at * 1000) > new Date()) {
        const { error } = await supabase.auth.signOut();
        if (error && !error.message.includes('session_not_found')) {
          console.warn('Supabase signout error:', error);
        }
      }
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      setUser(null);
      navigate('/login');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export { AuthProvider, useAuth };
